<script lang="ts">
    import type { LessonTime } from "@utils/practice/reminder-time-conversion"
    import TimeSliderSection from "./time-slider-section.svelte"
    import Icon from "@components/material/Icon.svelte"
    import { ReminderMemoryManagerSettings } from "@utils/practice/reminder-memory-manager"
    import { onDestroy, onMount } from "svelte"
    import { differenceInMinutes, startOfDay } from "date-fns"
    import type { MinutesAfterMidnight } from "@utils/practice/day-time"

    /**
     * We don't want it to be editable if the notifications are set
     */
    export let notificationsSet: boolean

    export let wakeup: MinutesAfterMidnight
    export let sleep: MinutesAfterMidnight

    export let bufferedWake: MinutesAfterMidnight
    export let bufferedSleep: MinutesAfterMidnight

    export let temptation: boolean = false
    export let temptationEnabled: boolean = false
    export let practiceTimes: (readonly [LessonTime, boolean, string, boolean])[]

    export let reactiveMemoryManager: ReminderMemoryManagerSettings

    export let mainAllowed: boolean
    export let hourlyAllowed: boolean
    export let freqAllowed: boolean

    $: thirtyBeforeBufferedWake = Math.max(bufferedWake - 30, 0)
    $: thirtyAfterBufferedSleep = Math.min(bufferedSleep + 30, 24 * 60)

    $: percentToMinute = (percent: number) => {
        const delta = thirtyAfterBufferedSleep - thirtyBeforeBufferedWake
        return thirtyBeforeBufferedWake + delta * percent
    }

    $: minuteToPercent = (minute: number) => {
        const delta = thirtyAfterBufferedSleep - thirtyBeforeBufferedWake
        const fromThirtyBeforeWake = minute - thirtyBeforeBufferedWake
        return fromThirtyBeforeWake / delta
    }

    $: widthOfTime = (minutes: number) => {
        return minuteToPercent(minutes) - minuteToPercent(0)
    }

    $: time = differenceInMinutes(new Date(), startOfDay(new Date()))
    onMount(() => {
        const interval = setInterval(() => {
            time = differenceInMinutes(new Date(), startOfDay(new Date()))
        }, 30 * 1000) // Every 30 seconds
        return clearInterval(interval)
    })

    /**
     * Nice display of hourly bars for reference
     */
    $: hours = Array.from(
        { length: Math.floor((thirtyAfterBufferedSleep - thirtyBeforeBufferedWake) / 60) + 1 },
        (_, i) => Math.ceil(thirtyBeforeBufferedWake / 60) * 60 + i * 60,
    )
        .filter((multiple) => multiple <= thirtyAfterBufferedSleep)
        .map(minuteToPercent)

    $: morningPercent = minuteToPercent(wakeup)
    $: eveningPercent = minuteToPercent(sleep)
</script>

<div class="relative w-full mt-4 mb-6">
    <div class="relative w-full h-4 border-2 dark:border-neutral-600 rounded-full">
        <!-- Line for current time -->
        <div class="relative h-full w-full rounded-full pointer-events-none">
            <!-- The block that preceeds the cursor -->
            <div
                class="absolute h-full bg-slate-500 opacity-25 -translate-x-1/2"
                style:left="{(minuteToPercent(time) - widthOfTime(30)) * 100}%"
                style:width="{widthOfTime(60) * 100}%"
            ></div>

            <!-- The cursor -->
            <div
                class="absolute h-full w-[2px] bg-slate-500 -translate-x-1/2 animate-pulse"
                style:left="{minuteToPercent(time) * 100}%"
            >
                <!-- The little triangle -->
                <div
                    class="w-2 h-2 bottom-full translate-y-1/2 -translate-x-1/2 left-1/2 absolute border-[7px] border-transparent border-t-slate-500"
                ></div>
            </div>
        </div>
        <!-- Lines for hour markers -->
        <div class="absolute left-0 top-0 h-full w-full overflow-hidden rounded-full">
            {#each hours as hour}
                <div
                    class="absolute h-full w-[2px] bg-slate-100 -translate-x-1/2"
                    style:left="{hour * 100}%"
                />
            {/each}
        </div>

        <!-- Response to temptation bar -->
        {#if temptation}
            <div
                aria-disabled={!temptationEnabled}
                class="aria-disabled:grayscale dark:aria-disabled:bg-neutral-700 aria-disabled:pointer-events-none absolute h-[2.5px] rounded-full bg-green-500 -bottom-2"
                style:left="{morningPercent * 100}%"
                style:right="{(1 - eveningPercent) * 100}%"
            />
        {/if}

        <div class="w-full -top-2 absolute h-0">
            <!-- Morning sun -->
            <div
                class="absolute bottom-0 opacity-50 -translate-x-1/2"
                style:left="{morningPercent * 100}%"
            >
                <Icon icon="sunset" class="w-5 relative" />
            </div>

            <!-- Noon -->
            <div
                class="absolute bottom-0 opacity-50 -translate-x-1/2"
                style:left="{minuteToPercent(12 * 60) * 100}%"
            >
                <small class="relative -bottom-1"> 12:00pm </small>
            </div>

            <!-- Moon -->
            <div
                class="absolute bottom-0 opacity-50 -translate-x-1/2"
                style:left="{eveningPercent * 100}%"
            >
                <Icon icon="moon" class="w-5 relative" />
            </div>
        </div>

        <!-- Practice times spread out over timeline, some with draggers -->
        {#each practiceTimes as [practice, enabled, classList, allowed]}
            <TimeSliderSection
                {reactiveMemoryManager}
                pulseBefore={time - 60}
                duration={0}
                lowerLimit={practice.individualMovability === 0 ? wakeup : bufferedWake}
                upperLimit={practice.individualMovability === 0 ? sleep : bufferedSleep}
                grayscale={notificationsSet}
                enabled={enabled && !notificationsSet}
                hidden={!enabled || !allowed}
                {minuteToPercent}
                {percentToMinute}
                {classList}
                {practice}
            />
        {/each}
    </div>
</div>
